import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../design/Button';
import Select, { SelectOption } from '../design/Select';
import { numberToHebrewLetters } from '../../app/utils/hebrewNumbers';
import {
  findPasuk,
  formatBookName,
  getChapterCount,
  getPasukCount,
  isHebrew,
  pasukKey,
  TORAH_BOOKS,
} from '../utils/pasuk';
import type { PasukRef } from '../types';

interface PasukPickerProps {
  value: PasukRef | null;
  onSave: (pasuk: PasukRef) => Promise<void>;
}

const range = (n: number): number[] => Array.from({ length: n }, (_, i) => i + 1);

/** Teacher control for the class's current pasuk (book → chapter → pasuk). */
const PasukPicker: React.FC<PasukPickerProps> = ({ value, onSave }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [draft, setDraft] = useState<PasukRef>(value ?? { book: 'Bereshit', chapter: 1, pasuk: 1 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (value) setDraft(value);
  }, [value]);

  const numberLabel = (n: number): string =>
    isHebrew(lang) ? numberToHebrewLetters(n) : String(n);

  const bookOptions: SelectOption[] = TORAH_BOOKS.map((b) => ({
    value: b,
    label: formatBookName(b, lang),
  }));
  const chapterOptions: SelectOption[] = useMemo(
    () =>
      range(getChapterCount(draft.book)).map((c) => ({ value: String(c), label: numberLabel(c) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draft.book, lang],
  );
  const pasukOptions: SelectOption[] = useMemo(
    () =>
      range(getPasukCount(draft.book, draft.chapter)).map((p) => ({
        value: String(p),
        label: numberLabel(p),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draft.book, draft.chapter, lang],
  );

  const preview = findPasuk(draft);
  const unchanged = value !== null && pasukKey(value) === pasukKey(draft);

  const save = async (): Promise<void> => {
    setSaving(true);
    setError(false);
    try {
      await onSave(draft);
    } catch (err) {
      console.error('[school] Failed to set current pasuk', err);
      setError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-3 gap-3">
        <Select
          label={t('school_book')}
          value={draft.book}
          options={bookOptions}
          onChange={(book: string): void => setDraft({ book, chapter: 1, pasuk: 1 })}
        />
        <Select
          label={t('school_chapter')}
          value={String(draft.chapter)}
          options={chapterOptions}
          onChange={(c: string): void => setDraft({ ...draft, chapter: Number(c), pasuk: 1 })}
        />
        <Select
          label={t('school_pasuk')}
          value={String(draft.pasuk)}
          options={pasukOptions}
          onChange={(p: string): void => setDraft({ ...draft, pasuk: Number(p) })}
        />
      </div>
      {preview && (
        <p
          dir="rtl"
          lang="he"
          className="rounded-xl bg-school-canvas p-3 font-pasuk text-lg leading-loose text-school-ink"
        >
          {preview.hebrew}
        </p>
      )}
      {error && (
        <p className="text-sm text-school-danger" role="alert">
          {t('school_error_save_pasuk')}
        </p>
      )}
      <Button icon="check" onClick={save} loading={saving} disabled={unchanged || !preview}>
        {t('school_set_current_pasuk')}
      </Button>
    </div>
  );
};

export default PasukPicker;
